// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { CSSProperties } from 'react';

const colors = {
  purple: '#2bb36f',
  purpleDark: '#77d7a7',
  purpleDarker: '#2bb36f',
  purpleLight: 'rgba(91, 78, 150, 0.4)',
  white: '#FFFFFF',
  textPrimary: '#1C1917',
  textSecondary: '#78716C',
  green: '#00D2AA',
  greenBullet: '#00D2AA',
  cardBg: '#FFFFFF',
  cardBorder: '#E7E5E4',
  progressBg: 'rgba(0,0,0,0.25)',
  progressFill: '#00D2AA',
  labelMuted: 'rgba(255,255,255,0.75)',
  frostBorder: 'rgba(255,255,255,0.25)',
  frostBg: 'rgba(255,255,255,0.12)'
} as const;

const fontG42Black = 'G42 Sans';

export const root: CSSProperties = {
  width: '100%',
  maxWidth: 900,
  margin: '0 auto',
  padding: '0 0 24px 0',
  boxSizing: 'border-box',
  fontFamily: fontG42Black
};

export const heroCard: CSSProperties = {
  background: `linear-gradient(145deg, ${colors.purple} 0%, ${colors.purpleDarker} 100%)`,
  borderRadius: 16,
  padding: '15px 25px',
  marginBottom: 24,
  display: 'flex',
  justifyContent: 'space-between',
  gap: 24,
  alignItems: 'center',
  flexWrap: 'wrap',
  boxSizing: 'border-box',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(45, 43, 110, 0.2)',
  border: '1px solid rgba(255, 255, 255, 0.06)'
};

export const heroLeft: CSSProperties = {
  flex: '1 1 280px'
};

export const synthesisBadge: CSSProperties = {
  display: 'inline-block',
  padding: '6px 14px',
  borderRadius: 8,
  backgroundColor: colors.frostBg,
  border: `1px solid ${colors.frostBorder}`,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.08em',
  color: colors.labelMuted,
  marginBottom: 16,
  textTransform: 'uppercase'
};

export const heroTitle: CSSProperties = {
  fontFamily: fontG42Black,
  fontSize: 28,
  fontWeight: 900,
  color: colors.white,
  margin: '0 0 14px 0',
  lineHeight: 1.25,
  letterSpacing: '-0.02em'
};

export const heroTitleLine2: CSSProperties = {
  display: 'block'
};

export const heroDesc: CSSProperties = {
  fontSize: 15,
  fontWeight: 400,
  color: 'rgba(255,255,255,0.82)',
  lineHeight: 1.55,
  margin: 0,
  maxWidth: 420
};

export const confidenceCard: CSSProperties = {
  flex: '0 0 auto',
  minWidth: 420,
  maxWidth: 440,
  padding: 22,
  borderRadius: 16,
  background: '#f7f4f1',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255,255,255,0.08)',
  boxSizing: 'border-box',
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
};

export const confidenceStarIcon: CSSProperties = {
  width: 46,
  height: 36,
  borderRadius: 10,
  backgroundColor: 'rgba(35, 32, 90, 0.9)',
  color: colors.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 14
};

export const confidenceLabel: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.1em',
  color: '#2A312B',
  marginBottom: 2,
  textTransform: 'uppercase',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginTop: 10
};

export const overallScore: CSSProperties = {
  fontSize: 20,
  fontWeight: 500,
  letterSpacing: '0.1em',
  color: '#2A312B',
  marginBottom: 10,
  alignItems: 'center',
  gap: 4
};
export const overallScoreLabel: CSSProperties = {
  marginBottom: 4
};
export const dimensionName: CSSProperties = {
  fontSize: 16,
  fontWeight: 500,
  marginBottom: 10
};

export const confidenceStarRow: CSSProperties = {
  // display: 'flex',
  alignItems: 'center',
  gap: 4,
  position: 'relative',
  top: 2
};

export const confidenceValue: CSSProperties = {
  fontFamily: fontG42Black,
  fontSize: 18,
  fontWeight: 500,
  color: '#2A312B',
  margin: '0 0 14px 0',
  lineHeight: 1.2
};

export const confidenceValueLine2: CSSProperties = {
  display: 'block'
};

export const progressBarWrap: CSSProperties = {
  height: 6,
  borderRadius: 3,
  backgroundColor: colors.progressBg,
  overflow: 'hidden',
  marginBottom: 2
};

export const progressBarFill: CSSProperties = {
  height: '100%',
  width: '78%',
  backgroundColor: colors.progressFill,
  borderRadius: 3,
  transition: 'width 0.3s ease'
};

export const progressLabels: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: 8,
  fontSize: 10,
  letterSpacing: '0.06em',
  color: colors.labelMuted,
  textTransform: 'uppercase',
  fontWeight: 600
};

export const twoCol: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: 24,
  marginBottom: 24
};

export const card: CSSProperties = {
  backgroundColor: colors.cardBg,
  border: `1px solid ${colors.cardBorder}`,
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
};

export const cardTitle: CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  color: colors.textPrimary,
  margin: '0 0 16px 0',
  display: 'flex',
  alignItems: 'center',
  gap: 10
};

export const cardIconGreen: CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: '50%',
  backgroundColor: 'rgba(0, 210, 170, 0.15)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: colors.green
};

export const cardIconGrey: CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: '50%',
  backgroundColor: '#F5F5F4',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: colors.textSecondary
};

export const bulletList: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0
};

export const bulletItem: CSSProperties = {
  display: 'flex',
  gap: 10,
  marginBottom: 12,
  fontSize: 14,
  lineHeight: 1.4,
  color: colors.textPrimary
};

export const bulletDotGreen: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: colors.greenBullet,
  flexShrink: 0,
  marginTop: 7
};

export const bulletDotGrey: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: colors.textSecondary,
  flexShrink: 0,
  marginTop: 7
};

export const cardContentWithButton: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 24
};

export const growthCard: CSSProperties = {
  ...card,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 24,
  flexWrap: 'wrap'
};

export const growthLeft: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  flex: '1 1 280px'
};

export const growthTitle: CSSProperties = {
  ...cardTitle,
  marginBottom: 8
};

export const growthDesc: CSSProperties = {
  fontSize: 14,
  color: colors.textSecondary,
  lineHeight: 1.5,
  margin: 0
};

export const viewPathsButton: CSSProperties = {
  padding: '12px 24px',
  borderRadius: 24,
  backgroundColor: colors.green,
  color: colors.white,
  border: 'none',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  boxShadow: '0 4px 14px rgba(0, 210, 170, 0.4)',
  flexShrink: 0,
  whiteSpace: 'nowrap',
  marginLeft: 'auto'
};

export const startNewButton: CSSProperties = {
  marginTop: 24,
  padding: '12px 24px',
  borderRadius: 24,
  backgroundColor: colors.green,
  color: colors.white,
  border: 'none',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  boxShadow: '0 4px 14px rgba(0, 210, 170, 0.4)'
};

export const summaryText: CSSProperties = {
  fontSize: 15,
  lineHeight: 1.5,
  color: 'rgb(28, 25, 23)',
  margin: '0 0 16px 0',
  fontWeight: 300
};

export const loadingMessage: CSSProperties = {
  textAlign: 'center',
  padding: 48,
  color: colors.textSecondary,
  fontSize: 16
};

export const errorMessage: CSSProperties = {
  ...loadingMessage,
  color: '#B91C1C'
};
